/**
 * AuthProvider + useAuth hook.
 *
 * Single source of truth for "who is signed in and what's their state."
 * Subscribes once to Firebase Auth's onAuthStateChanged at the top of the
 * app tree and exposes a stable React context for any descendant.
 *
 * States the consumer needs to handle:
 *   loading=true                 → don't render auth-gated UI yet
 *   user=null                    → signed out
 *   user=User, !emailVerified    → signed up but inbox-not-clicked-yet
 *   user=User, emailVerified     → fully signed in
 *
 * Role + status (pending-Director-approval) come from a separate
 * users/{uid} Firestore doc that lands in the next PR; this PR just handles
 * "did Firebase Auth verify the email."
 *
 * Note on emailVerified — Firebase does NOT re-fire onAuthStateChanged
 * when a user clicks the verification link in their email; we have to
 * reload the user manually to see the flip. The refresh() function does
 * exactly that and force-emits a new user reference so React re-renders.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from './firebase';
import {
  consumePendingRedirect,
  isRedirectReturnInFlight,
  refreshCurrentUser,
  SIGNING_OUT_CANCELLED_EVENT,
  SIGNING_OUT_EVENT,
} from './auth';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  /** True from the first sign-out click until the cleanup reload lands. */
  signingOut: boolean;
  /**
   * Last error surfaced from a redirect-based sign-in attempt. Set when
   * the user returns from Google's OAuth screen and Firebase rejects the
   * credential (e.g. unauthorized-domain, account-exists-with-different-credential).
   * Null in the common happy path.
   */
  redirectError: string | null;
  /**
   * True once the Hub's own 10 second deadline on the sign-in restore has
   * passed with no answer from Firebase. Never a sign-out: the restore keeps
   * running and this clears itself the moment an auth state arrives. It
   * exists so a boot that is waiting on nothing visible can say so on screen
   * instead of holding a blank page for the SDK's own 30 to 60 second timeout.
   */
  restoreTimedOut: boolean;
  /**
   * Set alongside restoreTimedOut, to tell the screen WHY the restore has
   * not answered. 'redirect' means the guard held off subscribing at all,
   * because a Google return is in flight or the Hub cannot rule one out
   * (lib/auth.ts, isRedirectReturnInFlight). On that path, unlike an
   * ordinary slow restore, tapping Try again cannot make Firebase answer
   * any sooner: the effect re-runs but re-awaits the SAME pending redirect
   * read, so the screen should say so rather than imply the button can
   * skip the wait. Null for an ordinary slow restore, and whenever
   * restoreTimedOut is false.
   */
  restoreTimedOutReason: 'redirect' | null;
  /**
   * Run the restore again after that deadline screen. Re-awaits the SAME
   * pending redirect read (consumePendingRedirect memoizes it in lib/auth.ts),
   * so a Google return that lands late is honoured here rather than lost, and
   * re-subscribes to Firebase. Signs nobody out.
   */
  retryRestore: () => void;
  /**
   * Force-reload the current user from Firebase. Used by the verification
   * card's "I clicked the link" button to pick up the emailVerified flip.
   * Returns the fresh user (or null if signed out).
   */
  refresh: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signingOut: false,
  redirectError: null,
  restoreTimedOut: false,
  restoreTimedOutReason: null,
  retryRestore: () => {},
  refresh: async () => null,
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [signingOut, setSigningOut] = useState(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const [restoreTimedOut, setRestoreTimedOut] = useState(false);
  const [restoreTimedOutReason, setRestoreTimedOutReason] = useState<'redirect' | null>(null);
  const [restoreAttempt, setRestoreAttempt] = useState(0);

  useEffect(() => {
    const begin = () => setSigningOut(true);
    const cancel = () => setSigningOut(false);
    window.addEventListener(SIGNING_OUT_EVENT, begin);
    window.addEventListener(SIGNING_OUT_CANCELLED_EVENT, cancel);
    return () => {
      window.removeEventListener(SIGNING_OUT_EVENT, begin);
      window.removeEventListener(SIGNING_OUT_CANCELLED_EVENT, cancel);
    };
  }, []);

  useEffect(() => {
    // If Firebase init failed earlier (missing env vars), auth is null. Skip
    // the subscription so we don't render a broken UI. The Firebase status
    // card on the landing page already surfaces the misconfig.
    if (!auth) {
      setLoading(false);
      return;
    }
    // If the user just came back from a signInWithRedirect (mobile Safari
    // path), Firebase needs us to call getRedirectResult to finalize the
    // sign-in. The result is also delivered via onAuthStateChanged, but
    // calling getRedirectResult is what surfaces any error (unauthorized
    // domain, etc.) and lets us show it on the UI instead of leaving the
    // user staring at the landing page wondering why nothing happened.
    const currentAuth = auth;
    let cancelled = false;
    let redirectFailed = false;
    let unsubscribe: (() => void) | undefined;
    let flashGuard: number | undefined;
    // With lazy OAuth initialization, a restored previous account can arrive
    // before a pending Google return. Do not publish that previous person.
    // Subscribe after the return settles: Firebase's first callback then gives
    // the latest authoritative user, and all later account changes stay live.
    // No callback still means loading, never an assumed signed-out state.
    const subscribe = () => {
      if (cancelled || unsubscribe) return;
      let initialState = true;
      unsubscribe = onAuthStateChanged(currentAuth, (nextUser) => {
        if (cancelled) return;
        // A slow but healthy restore answering right as the deadline fires
        // must not still paint the "took too long" card for one frame. The
        // deadline arms this on a short second timer rather than in its own
        // task; the first callback here beats it and cancels it before it
        // ever fires.
        if (flashGuard !== undefined) {
          window.clearTimeout(flashGuard);
          flashGuard = undefined;
        }
        setUser(nextUser);
        setLoading(false);
        // An answer, however late, is the end of the deadline screen below.
        setRestoreTimedOut(false);
        setRestoreTimedOutReason(null);
        // Restoring the previous person after a failed Google return does not
        // mean that return succeeded. Keep its error for the first callback;
        // a later successful sign-in can clear it normally.
        if (nextUser && (!initialState || !redirectFailed)) setRedirectError(null);
        initialState = false;
      });
    };
    // THE HUB'S OWN DEADLINE ON THE RESTORE (first opening audit, I-2).
    // Every other check on the first opening is bounded at ten seconds: the
    // dancer cookie check (lib/dancerSession.ts) and the account document
    // stall escape (lib/users.ts). This one was bounded by nothing of ours,
    // only by the SDK's own 30 to 60 second REST timeout, which is longer
    // than index.html's dead boot reload offer and longer than every other
    // wait on the boot. It is also the wait a signed-in returning phone
    // actually sits in.
    //
    // What the deadline does is deliberately small. It does NOT sign anyone
    // out, cancel the restore, or skip it: it subscribes to
    // onAuthStateChanged anyway, which is how the SDK delivers a restored
    // user whether or not the redirect read ever settles, and then says so
    // on screen. A redirect result that lands after the deadline is not lost
    // either. consumePendingRedirect() memoizes its one read of Firebase's
    // pending marker, so the .then below still runs when it settles, and the
    // retry path re-awaits that same promise rather than asking Firebase a
    // second time (which would answer null, the marker having been consumed).
    //
    // The one case the deadline must NOT subscribe in is the middle of a
    // Google return, which is the property PR #1145 bought and this must not
    // spend: Firebase hands back the PREVIOUS account first, so subscribing
    // there would open the app as the wrong person for a second before the
    // return landed. That case gets the screen and nothing else, right away:
    // there is no live subscription about to answer, so nothing could beat
    // the flash guard below, and showing the honest screen immediately is
    // the whole point of that branch.
    const deadline = window.setTimeout(() => {
      if (cancelled) return;
      if (isRedirectReturnInFlight()) {
        setRestoreTimedOutReason('redirect');
        setRestoreTimedOut(true);
        return;
      }
      subscribe();
      // Give the fresh subscription a short window to answer before
      // painting the card. See the comment on the onAuthStateChanged
      // callback above for why this exists and how it gets cancelled.
      flashGuard = window.setTimeout(() => {
        if (cancelled) return;
        setRestoreTimedOutReason(null);
        setRestoreTimedOut(true);
      }, 300);
    }, 10000);
    void consumePendingRedirect().catch((err) => {
      redirectFailed = true;
      if (!cancelled) {
        setRedirectError(err instanceof Error ? err.message : String(err));
      }
    }).then(() => {
      if (cancelled) return;
      window.clearTimeout(deadline);
      subscribe();
    });
    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
      if (flashGuard !== undefined) window.clearTimeout(flashGuard);
      unsubscribe?.();
    };
  }, [restoreAttempt]);

  const retryRestore = useCallback(() => {
    setRestoreTimedOut(false);
    setRestoreTimedOutReason(null);
    setRestoreAttempt((attempt) => attempt + 1);
  }, []);

  const refresh = useCallback(async () => {
    const fresh = await refreshCurrentUser();
    // setUser({ ...fresh }) won't compile against the User type; instead we
    // mirror the SDK's pattern of returning the same instance after reload()
    // but force a new reference for React via a no-op spread into a typed
    // intermediate. Simpler: clone via Object.create on the prototype so
    // type identity holds.
    if (fresh) {
      // The reload() call mutated `fresh` in place; setState with the same
      // reference would be a no-op. Use the auth.currentUser getter (which
      // may return the same object) but wrap in a sentinel — easiest is to
      // null then re-set on next tick.
      setUser(null);
      // microtask flush so React commits the null first
      await Promise.resolve();
      setUser(fresh);
    } else {
      setUser(null);
    }
    return fresh;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signingOut, redirectError, restoreTimedOut, restoreTimedOutReason, retryRestore, refresh }),
    [user, loading, signingOut, redirectError, restoreTimedOut, restoreTimedOutReason, retryRestore, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
